import classNames from 'classnames/bind';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';

import Button from 'uikit/Button';
import InputText from 'uikit/InputText';
import InputError from 'uikit/InputError';
import Select from 'uikit/Select';
import Switch from 'uikit/Switch';
import styles from './QuestionImportList.module.scss';

const cx = classNames.bind(styles);

const QuestionImportList = ({
  chapters,
  handleQuestionsImport,
  t,
  validationSchema,
}) => (
  <>
    <Formik
      initialValues={{
        chapterId: '',
        type: '',
        content: '',
        rowSeparator: 'newline',
        rowSeparatorOther: '-',
        columnSeparator: 'tab',
        columnSeparatorOther: '_',
        switchOrder: false,
      }}
      onSubmit={handleQuestionsImport}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, status, values }) => (
        <Form className={cx('subpage')}>
          {status === '500' && (
            <p className={cx('error')} role="alert">
              {t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/question-import-error',
              )}
            </p>
          )}
          <h3 className={cx('sub-title')}>
            <span className={cx('count')}>1</span>
            {t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/my-question',
            )}
          </h3>
          <Select
            className={cx('field')}
            icon="question"
            label={t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/type-label',
            )}
            name="type"
          >
            <option value="">
              {t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/no-type',
              )}
            </option>
            <option value="Flashcard">{t('Questions/Flashcard/name')}</option>
            <option value="MCQ">{t('Questions/MCQ/name')}</option>
            <option value="ShortAnswer">
              {t('Questions/ShortAnswer/name')}
            </option>
            <option value="Matching">{t('Questions/Matching/name')}</option>
            <option value="Categorization">
              {t('Questions/Categorization/name')}
            </option>
          </Select>
          <InputText
            className={cx('field')}
            label={t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/content-label',
            )}
            maxRows={20}
            minRows={4}
            name="content"
            type="textarea"
          />
          <h3 className={cx('sub-title', 'margin')}>
            <span className={cx('count')}>2</span>
            {t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/options',
            )}
          </h3>
          <div className={cx('separators')}>
            <div className={cx('separator', 'field')}>
              <p className={cx('label')}>
                {t(
                  'Course/Teacher/Editor/QuestionImport/QuestionImportList/column-separator-label',
                )}
              </p>
              <div className={cx('multi')}>
                <Select name="columnSeparator">
                  <option value="tab">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/tab',
                    )}
                  </option>
                  <option value=" ">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/space',
                    )}
                  </option>
                  <option value=",">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/comma',
                    )}
                  </option>
                  <option value="other">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/other',
                    )}
                  </option>
                </Select>
                {values.columnSeparator === 'other' && (
                  <InputText
                    className={cx('extra')}
                    name="columnSeparatorOther"
                    shouldHideError
                  />
                )}
              </div>
              <InputError
                className={cx('field-error')}
                name="columnSeparatorOther"
              />
            </div>
            <div className={cx('separator', 'field')}>
              <p className={cx('label')}>
                {t(
                  'Course/Teacher/Editor/QuestionImport/QuestionImportList/row-separator-label',
                )}
              </p>
              <div className={cx('multi')}>
                <Select name="rowSeparator">
                  <option value="newline">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/newline',
                    )}
                  </option>
                  <option value=";">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/semicolon',
                    )}
                  </option>
                  <option value="other">
                    {t(
                      'Course/Teacher/Editor/QuestionImport/QuestionImportList/other',
                    )}
                  </option>
                </Select>
                {values.rowSeparator === 'other' && (
                  <InputText
                    className={cx('extra')}
                    name="rowSeparatorOther"
                    shouldHideError
                  />
                )}
              </div>
              <InputError
                className={cx('field-error')}
                name="rowSeparatorOther"
              />
            </div>
            <div className={cx('separator', 'field', 'switch-button')}>
              <Switch name="switchOrder" />
              <p className={cx('label', 'switch-label')}>
                {t(
                  'Course/Teacher/Editor/QuestionImport/QuestionImportList/switch-order',
                )}
              </p>
            </div>
          </div>
          <h3 className={cx('sub-title', 'margin')}>
            <span className={cx('count')}>3</span>
            {t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/import-in',
            )}
          </h3>
          <Select
            className={cx('field')}
            icon="book-open"
            label={t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportList/chapter-label',
            )}
            name="chapterId"
          >
            <option value="">
              {t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/no-chapter',
              )}
            </option>
            {chapters.map((chapter) => (
              <option key={chapter.id} value={chapter.id}>
                {chapter.title}
              </option>
            ))}
          </Select>
          <div className={cx('actions')}>
            <div />
            <Button
              className={cx('action')}
              intent="primary"
              isLoading={isSubmitting}
              size="large"
              type="submit"
            >
              {t(
                'Course/Teacher/Editor/QuestionImport/QuestionImportList/confirm-import-questions',
              )}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  </>
);

QuestionImportList.propTypes = {
  chapters: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleQuestionsImport: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired,
};

export default QuestionImportList;
